import React, { useState } from "react";
import {
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Spinner,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input as ReactstrapInput,
  CardHeader,
  Row,
  Col,
  Collapse,
  Table,
  Alert,
} from "reactstrap";
import { formatDateTime } from "helpers/date_utils";

// Import the hooks you'll need
import {
  useUpdatePurchaseRequestItemsMutation,
  useApplyComparisonSelectionMutation,
  useGetStockManagementQuery,
  useGetProductsSupplierByIdQuery,
  useGetPruchaseOrdersByProductAndSupplierMutation
} from "slices/InventoryManagement/inventoryManagementSlice";
import PriceViewComponent, { PriceViewCompact } from "../CommonComponents/PriceViewComponent";
import { IsValidPermissions } from "Routes/AuthProtected";
import ProductImage from "../CommonComponents/ProductImage";
import ProductThumbnail from "../CommonComponents/ProductThumbnail";

// Define types for the openSections state
type SectionKeys = 'lowest_cost' | 'previous_orders_mapped';

interface OpenSectionsState {
  lowest_cost: boolean;
  previous_orders_mapped: boolean;
}

interface OrderItem {
  po_number: string;
  po_id: number;
  po_status: string;
  po_created_date: string;
  item_id: number;
  product_id: number;
  product_name: string;
  ordered_quantity: number;
  delivered_quantity: number;
  accepted_quantity: number;
  item_status: string;
  delivery_status: string;
  expected_delivery_date: string;
  actual_delivery_date: string;
  unit_price: number;
  total_price: number;
}

interface OrderData {
  supplier: {
    id: number;
    name: string;
  };
  product: {
    id: number;
    name: string;
    code: string | null;
  };
  // Backward-compatible field name from old API (now equals total_orders_count)
  last_3_orders_count: number;
  // New pagination meta from API
  total_orders_count: number;
  page: number;
  page_size: number;
  total_pages: number;
  orders: Array<{
    po_id: number;
    po_number: string;
    po_status: string;
    created_date: string;
    total_cost: number;
    items: OrderItem[];
  }>;
}

const PurchaseOrderProduct = (props: any) => {
  const {
    item,
    refetchPurchaseProducts,
    purchaseOrderStatus,
    targetProjectId,
    isSelectable = false,
    isSelected = false,
    onToggleSelect,
  } = props;
  
  // Disable supplier selection if purchase order status is rejected
  const isSupplierSelectionDisabled = purchaseOrderStatus?.toLowerCase() === "rejected";

  // State for modal and selected supplier
  const [modal, setModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<any>(null);
  const [totalCost, setTotalCost] = useState("");
  const [isRFQChecked, setIsRFQChecked] = useState(false);
  const [selectedStockSources, setSelectedStockSources] = useState<Map<number, number>>(new Map());
  // Backward compat aliases
  const selectedStockSourceId = selectedStockSources.size > 0 ? [...selectedStockSources.keys()][0] : null;
  const stockQty = String([...selectedStockSources.values()].reduce((a, b) => a + b, 0) || "");

  // State for order history modal
  const [orderHistoryModal, setOrderHistoryModal] = useState(false);
  const [selectedSupplierForHistory, setSelectedSupplierForHistory] = useState<any>(null);
  const [orderHistoryData, setOrderHistoryData] = useState<OrderData | null>(null);
  const [orderHistoryPage, setOrderHistoryPage] = useState<number>(1);
  const [orderHistoryPageSize] = useState<number>(5);

  // State for collapsible sections with proper typing
  const [openSections, setOpenSections] = useState<OpenSectionsState>({
    lowest_cost: true,
    previous_orders_mapped: true
  });

  // RTK Query hooks
  const [updatePurchaseRequestItem, { isLoading: updating }] =
    useUpdatePurchaseRequestItemsMutation();
  const [applyComparisonSelection] = useApplyComparisonSelectionMutation();

  const { data: stockManagementData, isLoading: allocatableStockLoading } =
    useGetStockManagementQuery(
      { page: 1, pageSize: 1000, search: "" },
      {
        skip: !modal || !item.product,
      }
    );

  const stockOptions = React.useMemo(() => {
    const rows = Array.isArray(stockManagementData)
      ? stockManagementData
      : stockManagementData?.results || [];
    return rows.filter((r: any) => {
      if (Number(r?.product) !== Number(item.product)) return false;
      if (Number(r?.balance_quantity || 0) <= 0) return false;
      if (targetProjectId == null || targetProjectId === "") return true;
      const isCrossProject = String(r?.source_project) !== String(targetProjectId);
      return !isCrossProject || !!r?.is_transfer_to_another_project;
    });
  }, [stockManagementData, item.product, targetProjectId]);

  const totalStockQty = [...selectedStockSources.values()].reduce((a, b) => a + b, 0);
  const stockQtyNum = totalStockQty;
  const requestedQty = parseFloat(item.quantity || "0") || 0;
  const supplierQtyNum = Math.max(0, requestedQty - stockQtyNum);

  // Fetch product suppliers
  const {
    data: productSuppliersData,
    isLoading: productSuppliersLoading,
    isError: productSuppliersError,
  } = useGetProductsSupplierByIdQuery(item.product, {
    skip: !item.product || !modal, // Only fetch when modal is open and product ID is available
  });

  // Fetch purchase orders for selected supplier and product using mutation
  const [
    getPurchaseOrdersByProductAndSupplier,
    {
      isLoading: orderHistoryLoading,
      isError: orderHistoryError
    }
  ] = useGetPruchaseOrdersByProductAndSupplierMutation();

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      // If opening modal and supplier already exists, pre-fill the data
      if (item.supplier_name) {
        // Find the current supplier from the suppliers list to pre-select it
        if (productSuppliersData) {
          const currentSupplier = productSuppliersData.suppliers.find(
            (supplier: any) => supplier.supplier_id === item.supplier
          );
          if (currentSupplier) {
            const supplierWithFinalPrice = {
              ...currentSupplier,
              finalAmount: currentSupplier.final_cost || 0,
            };
            setSelectedSupplier(supplierWithFinalPrice);
            setTotalCost(item.total_item_cost || "");
          }
        }
      } else {
        setSelectedSupplier(null);
        setTotalCost("");
      }
      // Set RFQ checkbox state based on current item
      setIsRFQChecked(item.is_rfq || false);
      setSelectedStockSources(new Map());
    }
  };

  // Toggle order history modal
  const toggleOrderHistoryModal = async (supplier?: any) => {
    if (supplier) {
      setSelectedSupplierForHistory(supplier);
      setOrderHistoryPage(1);
      setOrderHistoryModal(true);

      // Fetch order history when modal opens
      try {
        const result = await getPurchaseOrdersByProductAndSupplier({
          supplier_id: supplier.supplier_id,
          product_id: item.product,
          page: 1,
          page_size: orderHistoryPageSize,
        }).unwrap();
        setOrderHistoryData(result as OrderData);
      } catch (error) {
        console.error("Failed to fetch order history:", error);
        setOrderHistoryData(null);
      }
    } else {
      setSelectedSupplierForHistory(null);
      setOrderHistoryData(null);
      setOrderHistoryModal(false);
    }
  };

  // Refetch order history
  const refetchOrderHistory = async () => {
    if (selectedSupplierForHistory && item.product) {
      try {
        const result = await getPurchaseOrdersByProductAndSupplier({
          supplier_id: selectedSupplierForHistory.supplier_id,
          product_id: item.product,
          page: orderHistoryPage,
          page_size: orderHistoryPageSize,
        }).unwrap();
        setOrderHistoryData(result as OrderData);
      } catch (error) {
        console.error("Failed to refetch order history:", error);
      }
    }
  };

  // Toggle collapsible sections with proper typing
  const toggleSection = (sectionKey: SectionKeys) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Group suppliers by filter type
  const groupedSuppliers = React.useMemo(() => {
    if (!productSuppliersData?.suppliers) return {};

    const groups: any = {};

    productSuppliersData.suppliers.forEach((supplier: any) => {
      // Skip suppliers without available price
      if (!supplier.total_amount || supplier.total_amount <= 0) {
        return;
      }

      if (!groups[supplier.filter_by]) {
        groups[supplier.filter_by] = [];
      }

      // Check if supplier already exists in this group to avoid duplicates
      const exists = groups[supplier.filter_by].find(
        (s: any) => s.supplier_id === supplier.supplier_id
      );

      if (!exists) {
        groups[supplier.filter_by].push({
          ...supplier,
          finalAmount: supplier.final_cost || 0,
          hasPositiveAmount: (supplier.total_amount || 0) > 0,
        });
      }
    });

    return groups;
  }, [productSuppliersData]);

  // Get group display names
  const getGroupDisplayName = (filterBy: string) => {
    const displayNames: Record<string, string> = {
      lowest_cost: "Lowest Cost Suppliers",
      previous_orders_mapped: "Suppliers with Order History",
    };
    return (
      displayNames[filterBy] ||
      filterBy.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  // Get group description
  const getGroupDescription = (filterBy: string) => {
    const descriptions: Record<string, string> = {
      lowest_cost: "Suppliers offering the best prices for this product",
      previous_orders_mapped:
        "Suppliers you've successfully worked with before",
    };
    return descriptions[filterBy] || "";
  };

  // Process suppliers for recommendation (across all groups)
  const processedSuppliers = React.useMemo(() => {
    const allSuppliers = productSuppliersData?.suppliers || [];

    // Filter suppliers with total_amount > 0 first
    const suppliersWithPositiveAmount = allSuppliers.filter(
      (supplier: any) => (supplier.total_amount || 0) > 0
    );

    const suppliersWithFinalPrice = suppliersWithPositiveAmount.map(
      (supplier: any) => ({
        ...supplier,
        finalAmount: supplier.final_cost || 0,
      })
    );

    // Find the best price (lowest final amount) only from suppliers with total_amount > 0
    const bestPrice =
      suppliersWithPositiveAmount.length > 0
        ? Math.min(
          ...suppliersWithPositiveAmount.map((s: any) => s.finalAmount)
        )
        : null;

    // Mark suppliers with best price as recommended (only if they have total_amount > 0)
    return suppliersWithFinalPrice.map((supplier: any) => ({
      ...supplier,
      isRecommended:
        bestPrice !== null &&
        supplier.finalAmount === bestPrice &&
        (supplier.total_amount || 0) > 0,
      hasPositiveAmount: (supplier.total_amount || 0) > 0,
    }));
  }, [productSuppliersData]);

  // Find if a supplier is recommended
  const isSupplierRecommended = (supplier: any) => {
    return processedSuppliers.find(
      (s: any) => s.supplier_id === supplier.supplier_id && s.isRecommended
    );
  };

  const handleSupplierSelect = (supplier: any) => {
    if (isRFQChecked) {
      // Don't allow supplier selection if RFQ is checked
      return;
    }
    setSelectedSupplier(supplier);
    // Pre-fill based on remaining supplier quantity after stock allocation
    const calculatedTotal = supplier.finalAmount * Math.max(0, supplierQtyNum || requestedQty);
    setTotalCost(calculatedTotal.toFixed(2));
  };

  const handleStockSelect = (stock: any) => {
    if (isRFQChecked) return;
    setSelectedStockSources((prev) => {
      const next = new Map(prev);
      if (next.has(stock.id)) {
        next.delete(stock.id);
      } else {
        const alreadyAllocated = [...next.values()].reduce((a, b) => a + b, 0);
        const remaining = Math.max(0, requestedQty - alreadyAllocated);
        if (remaining <= 0) return prev;
        const canAllocate = Math.min(Number(stock.balance_quantity || 0), remaining);
        if (canAllocate > 0) next.set(stock.id, canAllocate);
      }
      return next;
    });
    setTimeout(() => {
      if (selectedSupplier) {
        const newTotal = [...selectedStockSources.values()].reduce((a, b) => a + b, 0);
        const recalculatedTotal =
          selectedSupplier.finalAmount * Math.max(0, requestedQty - newTotal);
        setTotalCost(recalculatedTotal.toFixed(2));
      }
    }, 0);
  };

  const handleRFQChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setIsRFQChecked(isChecked);

    if (isChecked) {
      setSelectedSupplier(null);
      setTotalCost("");
      setSelectedStockSources(new Map());
    }
  };

  const handleSave = async () => {
    const qty = parseFloat(item.quantity || "0") || 0;
    const stockQtyNum = parseFloat(stockQty || "0") || 0;

    const hasStockSelection = selectedStockSources.size > 0 && stockQtyNum > 0;

    if (!isRFQChecked) {
      if (!hasStockSelection && (!selectedSupplier || !totalCost)) {
        alert("Please select a supplier and enter total cost.");
        return;
      }
      if (hasStockSelection && stockQtyNum > qty) {
        alert("Stock quantity cannot exceed requested quantity.");
        return;
      }
      if (hasStockSelection && supplierQtyNum > 0 && !selectedSupplier) {
        alert("Please select supplier for remaining quantity.");
        return;
      }
    }

    try {
      const productData: any = {
        is_rfq: isRFQChecked,
      };

      // Only include supplier and cost data if not RFQ and supplier is selected
      if (!isRFQChecked && selectedSupplier) {
        productData.supplier = selectedSupplier.supplier_id;
        productData.total_item_cost = parseFloat(totalCost || "0");
      } else if (isRFQChecked) {
        // If RFQ is checked, clear supplier and cost data
        productData.supplier = null;
        productData.total_item_cost = 0;
      }

      if (isRFQChecked) {
        await updatePurchaseRequestItem({ id: item.id, productData }).unwrap();
      } else {
        const unitPrice = selectedSupplier?.finalAmount || 0;
        const supplierQty = Math.max(0, qty - stockQtyNum);
        const payloadItem: any = {
          purchase_request_item_id: item.id,
          discount: "0",
        };
        const stockSourcesArray = [...selectedStockSources.entries()].map(
          ([stockId, qty]) => ({ stock_id: stockId, qty: String(qty) })
        );
        if (!hasStockSelection) {
          payloadItem.source_mode = "supplier";
          payloadItem.supplier_id = selectedSupplier?.supplier_id;
          payloadItem.unit_price = String(unitPrice);
        } else if (supplierQty > 0) {
          payloadItem.source_mode = "split";
          payloadItem.supplier_id = selectedSupplier?.supplier_id;
          payloadItem.unit_price = String(unitPrice);
          payloadItem.stock_sources = stockSourcesArray;
          payloadItem.stock_qty = String(stockQtyNum);
          payloadItem.supplier_qty = String(supplierQty);
        } else {
          payloadItem.source_mode = "stock";
          payloadItem.stock_sources = stockSourcesArray;
          payloadItem.stock_qty = String(stockQtyNum);
          payloadItem.supplier_qty = "0";
        }
        await applyComparisonSelection({
          purchaseRequestId: item.purchase_request,
          items: [payloadItem],
        }).unwrap();
      }
      refetchPurchaseProducts();
      toggleModal();
    } catch (error) {
      console.error("Failed to update purchase request item:", error);
      const apiError = error as any;
      const backendMessage =
        apiError?.data?.detail ||
        apiError?.data?.error ||
        apiError?.error ||
        "Failed to update item. Please try again.";
      alert(String(backendMessage));
    }
  };

  const calculateCostPerUnit = () => {
    if (!totalCost || !item.quantity) return "0.00";
    const cost = parseFloat(totalCost) / parseFloat(item.quantity);
    return cost.toFixed(2);
  };

  // Calculate total with discount for suppliers
  const calculateRecommendedTotal = (supplier: any, supplierQty: number) => {
    return (supplier.finalAmount * supplierQty).toFixed(2);
  };

  // Format date for display
  const formatDateDisplay = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format date with time for order history
  const formatDateTimeDisplay = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if PO is created - hide all buttons if true
  const isPOCreated = item.is_po_created;

  // Check if RFQ is true - show select supplier button
  const isRFQ = item.is_rfq;

  // Check if select supplier button should be shown
  const shouldShowSelectSupplier = () => {
    // Don't show if supplier selection is disabled (e.g., when status is rejected)
    if (isSupplierSelectionDisabled) return false;
    // Show select supplier button only if:
    // 1. PO is not created AND
    // 2. RFQ is true OR no supplier is currently mapped
    return !isPOCreated && (isRFQ || !item.supplier_name);
  };

  // In split mode, price and supplier-side calculations should use only supplier quantity.
  const getSupplierQuantityForItem = (rowItem: any) => {
    const mode = String(rowItem?.source_mode || "").toLowerCase();
    if (mode === "split") {
      return parseFloat(rowItem?.supplier_allocation_quantity || "0") || 0;
    }
    if (mode === "stock") {
      return 0;
    }
    const supplierQty =
      parseFloat(rowItem?.supplier_allocation_quantity || "0") || 0;
    if (supplierQty > 0) return supplierQty;
    return parseFloat(rowItem?.quantity || "0") || 0;
  };

  // Check if edit button should be shown
  const shouldShowEditButton = () => {
    // Show edit button only if:
    // 1. PO is not created AND
    // 2. Supplier exists AND
    // 3. RFQ is false
    return !isPOCreated && item.supplier_name && !isRFQ;
  };

  // Check if there are any suppliers with available prices
  const hasSuppliersWithPrices = Object.keys(groupedSuppliers).length > 0;

  return (
    <React.Fragment>
      <tr>
        <td className="text-center">
          {isSelectable ? (
            <Input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
            />
          ) : null}
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0 avatar-md bg-light rounded p-1">
              <ProductThumbnail
      attachment_urls={item?.attachment_urls}
      alt={item?.product_name || ''}
      size="lg"
      className="me-2"
    />
              {/* {item.thumbnail_url ? (
                <img
                  src={item.thumbnail_url}
                  alt={item.product_name}
                  className="img-fluid d-block rounded"
                  style={{ width: "60px", height: "60px", objectFit: "cover" }}
                />
              ) : null}
              {!item.thumbnail_url && (
                <div className="avatar-title bg-soft-primary text-primary rounded w-100 h-100 d-flex align-items-center justify-content-center">
                  <i className="ri-box-3-line fs-18"></i>
                </div>
              )} */}
            </div>
            <div className="flex-grow-1 ms-3">
              <h6 className="fs-14 mb-1">
                <span className="text-dark">{item.product_name}</span>
                {isRFQ && (
                  <Badge color="warning" className="ms-2 fs-10">
                    RFQ
                  </Badge>
                )}
                {isPOCreated && (
                  <Badge color="success" className="ms-2 fs-10">
                    <i className="ri-check-double-line me-1"></i>
                    PO Created
                  </Badge>
                )}
              </h6>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {item.category_name && (
                  <Badge color="primary" className="badge-border">
                    <i className="ri-price-tag-3-line align-middle me-1"></i>
                    {item.category_name}
                  </Badge>
                )}
                {item.measurement_name && (
                  <Badge color="primary" className="badge-border">
                    <i className="ri-ruler-line align-middle me-1"></i>
                    {item.measurement_name}
                  </Badge>
                )}
              </div>
              {item.tag_names && item.tag_names.length > 0 && (
                <div className="mt-1">
                  <small className="text">
                    Tags: {item.tag_names.join(", ")}
                  </small>
                </div>
              )}
              <div className="mt-1">
                <small className="text-muted">
                  Product ID: {item.product} • Item ID: {item.id}
                </small>
              </div>
            </div>
          </div>
        </td>
        <td>
          <div className="text-center">
            <span className="fw-bold fs-16 text-dark">
              {["split", "stock"].includes(
                String(item?.source_mode || "").toLowerCase()
              )
                ? getSupplierQuantityForItem(item)
                : item.quantity}
            </span>
            {["split", "stock"].includes(
              String(item?.source_mode || "").toLowerCase()
            ) && (
              <div className="text-muted fs-11 mt-1">
                Requested: {item.quantity} | Supplier:{" "}
                {getSupplierQuantityForItem(item)} | Stock:{" "}
                {item.stock_allocation_quantity || 0}
              </div>
            )}
            {item.measurement_name && (
              <div className="text-muted fs-12">{item.measurement_name}</div>
            )}
          </div>
        </td>
        <td>
          <div className="text-center">
            {item.expected_delivery_date ? (
              <div>
                <span className="badge bg-soft-success text-success fw-medium">
                  <i className="ri-calendar-line align-middle me-1"></i>
                  {new Date(item.expected_delivery_date).toLocaleDateString()}
                </span>
                <div className="text-muted fs-12 mt-1">
                  {new Date(item.expected_delivery_date).toLocaleDateString(
                    "en-US",
                    {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    }
                  )}
                </div>
              </div>
            ) : (
              <span className="text-muted fst-italic">Not specified</span>
            )}
          </div>
        </td>
        <td>
          <div className="max-w-200">
            {item.delivery_note ? (
              <div>
                <span className="text-dark fw-medium">Note:</span>
                <p className="text-muted mb-0 fs-13">{item.delivery_note}</p>
              </div>
            ) : (
              <span className="text-muted fst-italic">No notes</span>
            )}
          </div>
        </td>

        {/* Supplier Column */}
        <td>
          <div className="text-center">
            {item.source_mode === "stock" || item.source_mode === "split" ? (
              <div className="d-flex flex-column align-items-center">
                {item.supplier_name && Number(getSupplierQuantityForItem(item)) > 0 && (
                  <div className="fw-medium text-dark mb-1">{item.supplier_name}</div>
                )}
                <div className="d-flex align-items-center justify-content-center gap-2">
                  <Badge color="info" className="fs-12">
                    Stock {item.stock_allocation_quantity || 0}
                  </Badge>
                  {!!item.supplier_allocation_quantity && Number(item.supplier_allocation_quantity) > 0 && (
                    <Badge color="primary" className="fs-12">
                      Supplier {item.supplier_allocation_quantity}
                    </Badge>
                  )}
                  {(IsValidPermissions(["EDIT_PURCHASE_ORDERFULLFILMENT"])) && !isPOCreated && !isSupplierSelectionDisabled && (
                    <Button
                      color="link"
                      size="sm"
                      onClick={toggleModal}
                      disabled={updating}
                      className="text-primary p-0"
                      title="Edit allocation"
                    >
                      <i className="ri-edit-2-line fs-16"></i>
                    </Button>
                  )}
                </div>
                {!!item?.stock_allocation_references?.length && (
                  <div className="mt-1 text-start">
                    {item.stock_allocation_references.map((alloc: any) => (
                      <div key={alloc.id} className="text-muted fs-11">
                        {alloc.source_stock_reference || alloc.allocation_no || `Alloc ${alloc.id}`} | Qty {alloc.allocated_from_stock_qty}
                        {alloc.purchase_order_number ? ` | PO ${alloc.purchase_order_number}` : ""}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : item.supplier_name ? (
              <div className="d-flex align-items-center justify-content-center">
                <div className="text-start">
                  <span className="fw-medium text-dark">
                    {item.supplier_name}
                  </span>
                  {item.supplier_contact && (
                    <div className="text-muted fs-12">
                      {item.supplier_contact}
                    </div>
                  )}
                  {item.supplier_email && (
                    <div className="text-muted fs-12">
                      {item.supplier_email}
                    </div>
                  )}
                </div>
                {/* Show edit button only if PO is not created */}
                {(IsValidPermissions(["EDIT_PURCHASE_ORDERFULLFILMENT"])) && shouldShowEditButton() && (
                  <Button
                    color="link"
                    size="sm"
                    onClick={toggleModal}
                    disabled={updating}
                    className="text-primary ms-2 p-0"
                    title="Edit supplier"
                  >
                    <i className="ri-edit-2-line fs-16"></i>
                  </Button>
                )}
              </div>
            ) : isRFQ ? (
              <div>
                <Badge color="warning" className="fs-12 mb-2 mt-1">
                  RFQ
                </Badge>
                {/* Show select supplier button for RFQ items if PO is not created and not rejected */}
                {(IsValidPermissions(["EDIT_PURCHASE_ORDERFULLFILMENT"])) && !isPOCreated && !isSupplierSelectionDisabled && (
                  <Button
                    color="primary"
                    size="sm"
                    onClick={toggleModal}
                    disabled={updating}
                    className="mt-1"
                  >
                    {updating ? <Spinner size="sm" /> : "Select Supplier"}
                  </Button>
                )}
              </div>
            ) : shouldShowSelectSupplier() ? (
              <>
              {IsValidPermissions(["EDIT_PURCHASE_ORDERFULLFILMENT"]) &&
              <Button
                color="primary"
                size="sm"
                onClick={toggleModal}
                disabled={updating || isPOCreated}
              >
                {updating ? <Spinner size="sm" /> : "Select Supplier"}
              </Button>
              }
              </>
            ) : isPOCreated ? (
              <Badge color="success" className="fs-12">
                <i className="ri-check-double-line me-1"></i>
                PO Created
              </Badge>
            ) : (
              <span className="text-muted fst-italic">No supplier</span>
            )}
          </div>
        </td>

        {/* Total Cost Column */}
        <td>
          <div className="text-center">
            {item.total_item_cost ? (
              <div>
                {/* <span className="fw-bold text-success fs-16">
                  <PriceViewCompact price={parseFloat(item.total_item_cost).toFixed(2)}/>
                </span> */}
                {item.quantity && (
                  <div className="text-muted fs-12">
                    <PriceViewComponent
                      price={item.total_item_cost}
                      quantity={Math.max(getSupplierQuantityForItem(item), 1)}
                      unitPrice={item.effective_unit_price ?? item.unit_price}
                      showPerUnit={true}
                    />

                  </div>
                )}
              </div>
            ) : (
              <span className="text-muted fst-italic">Not set</span>
            )}
          </div>
        </td>
      </tr>

      {/* Supplier Selection Modal */}
      {/* Only show modal if PO is not created */}
      {!isPOCreated && (
        <Modal isOpen={modal} toggle={toggleModal} size="lg">
          <ModalHeader toggle={toggleModal}>
            {item.supplier_name ? "Edit Supplier for" : "Select Supplier for"}{" "}
            {item.product_name}
          </ModalHeader>
          <ModalBody>
            {/* RFQ Option */}
            <FormGroup check className="mb-4">
              <ReactstrapInput
                type="checkbox"
                id="rfq-checkbox"
                checked={isRFQChecked}
                onChange={handleRFQChange}
              />
              <Label for="rfq-checkbox" check className="fw-medium">
                <Badge color="warning" className="me-2">
                  RFQ
                </Badge>
                Request for Quotation - Mark this item for RFQ process
              </Label>
              <div className="text-muted small mt-1">
                {isRFQChecked
                  ? "RFQ mode enabled - Supplier selection is disabled. This item will be marked for quotation process."
                  : "When checked, this item will be marked for RFQ and no supplier will be assigned."}
              </div>
            </FormGroup>

            {/* All Suppliers List - Only show if RFQ is not checked */}
            {!isRFQChecked && (
              <div className="mb-4">
                <Card className="mb-3 border">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div>
                        <h6 className="mb-1">Stock Allocation</h6>
                        <small className="text-muted">
                          Allocate stock first, then buy only the remaining quantity from supplier.
                        </small>
                      </div>
                      {selectedStockSources.size > 0 && (
                        <Button
                          color="light"
                          size="sm"
                          className="border"
                          onClick={() => {
                            setSelectedStockSources(new Map());
                            if (selectedSupplier) {
                              const resetTotal =
                                selectedSupplier.finalAmount * requestedQty;
                              setTotalCost(resetTotal.toFixed(2));
                            }
                          }}
                        >
                          <i className="ri-close-line me-1"></i>
                          Use Supplier Only
                        </Button>
                      )}
                    </div>
                    <div className="bg-soft-primary border rounded p-2 mb-3">
                      <Row className="g-2 text-center">
                        <Col md={4}>
                          <div className="text-muted fs-11">Requested</div>
                          <div className="fw-semibold">{requestedQty}</div>
                        </Col>
                        <Col md={4}>
                          <div className="text-muted fs-11">From Stock</div>
                          <div className="fw-semibold text-primary">{stockQtyNum}</div>
                        </Col>
                        <Col md={4}>
                          <div className="text-muted fs-11">Need Supplier</div>
                          <div className="fw-semibold text-success">{supplierQtyNum}</div>
                        </Col>
                      </Row>
                    </div>
                    {allocatableStockLoading ? (
                      <div className="text-muted">Loading stock...</div>
                    ) : stockOptions.length === 0 ? (
                      <Alert color="warning" className="mb-0">
                        No transferable stock available for this product.
                      </Alert>
                    ) : (
                      <div className="d-flex flex-column gap-2">
                        {stockOptions.map((s: any) => {
                          const isSelected = selectedStockSources.has(s.id);
                          const requestedQty = parseFloat(item.quantity || "0") || 0;
                          const rowMaxQty = Math.min(
                            Number(s.balance_quantity || 0),
                            requestedQty
                          );
                          const isCrossProject =
                            targetProjectId != null &&
                            targetProjectId !== "" &&
                            String(s?.source_project) !== String(targetProjectId);
                          return (
                            <div
                              key={s.id}
                              className={`p-3 rounded border cursor-pointer ${
                                isSelected
                                  ? "border-primary bg-soft-primary shadow-sm"
                                  : "border-light"
                              }`}
                              onClick={() => handleStockSelect(s)}
                            >
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                    <div className="fw-semibold">
                                      {s.stock_reference_number || `Stock ${s.id}`}
                                    </div>
                                    <Badge color="light" className="text-dark border">
                                      Balance: {s.balance_quantity}
                                    </Badge>
                                  </div>
                                  <div className="text-muted fs-12">
                                    Source Project: {s.source_project_name || "N/A"}
                                  </div>
                                  <div className="text-muted fs-12">
                                    Source PR: {s.source_purchase_request_number || "-"}
                                  </div>
                                  <div className="text-muted fs-12">
                                    <i className="ri-box-3-line me-1"></i>
                                    Stock Type: {s.stock_type_display || s.stock_type || "-"}
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  {isCrossProject && (
                                    <Badge color="warning" className="fs-11">
                                      Cross Project
                                    </Badge>
                                  )}
                                  {isSelected ? (
                                    <i className="ri-checkbox-fill text-primary fs-18"></i>
                                  ) : (
                                    <i className="ri-checkbox-blank-line text-muted fs-18"></i>
                                  )}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="mt-2 p-2 rounded bg-light border">
                                  <small className="fw-medium text-success">
                                    <i className="ri-check-line me-1"></i>
                                    Allocating: {selectedStockSources.get(s.id) || 0} of {s.balance_quantity} available
                                  </small>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardBody>
                </Card>

                <h6 className="mb-3">Available Suppliers</h6>

                {productSuppliersLoading ? (
                  <div className="text-center py-3">
                    <Spinner size="sm" color="primary" />
                    <div className="mt-2 text-muted">Loading suppliers...</div>
                  </div>
                ) : productSuppliersError ? (
                  <div className="text-center text-muted py-3">
                    <i className="ri-error-warning-line fs-18"></i>
                    <div>Failed to load suppliers</div>
                  </div>
                ) : hasSuppliersWithPrices ? (
                  <div className="suppliers-list">
                    {Object.keys(groupedSuppliers).map((filterBy) => {
                      // Type guard to ensure filterBy is a valid SectionKeys
                      const sectionKey = filterBy as SectionKeys;

                      return (
                        <div key={filterBy} className="mb-4">
                          <Card className="border-0 shadow-sm">
                            <CardHeader
                              className="bg-light cursor-pointer"
                              onClick={() => toggleSection(sectionKey)}
                            >
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <h6 className="mb-0 text-primary">
                                    {getGroupDisplayName(filterBy)}
                                    <i
                                      className={`ri-arrow-${openSections[sectionKey] ? 'down' : 'right'}-s-line ms-2`}
                                    ></i>
                                  </h6>
                                  <small className="text-muted">
                                    {getGroupDescription(filterBy)}
                                  </small>
                                </div>
                                <Badge color="outline-primary">
                                  {groupedSuppliers[filterBy].length} supplier{groupedSuppliers[filterBy].length !== 1 ? 's' : ''}
                                </Badge>
                              </div>
                            </CardHeader>
                            <Collapse isOpen={openSections[sectionKey]}>
                              <CardBody className="p-0">
                                {groupedSuppliers[filterBy].map((supplier: any) => (
                                  <div
                                    key={`${supplier.supplier_id}-${supplier.mapping_id}`}
                                    className={`p-3 border-bottom cursor-pointer ${selectedSupplier?.supplier_id === supplier.supplier_id
                                      ? "border-primary bg-soft-primary"
                                      : "border-light"
                                      } ${item.supplier === supplier.supplier_id
                                        ? "border-success bg-soft-success"
                                        : ""
                                      } ${isRFQChecked ? "opacity-50" : ""}`}
                                    onClick={() => handleSupplierSelect(supplier)}
                                  >
                                    <div className="d-flex justify-content-between align-items-start">
                                      <div className="flex-grow-1">
                                        <div className="d-flex align-items-center mb-2">
                                          <h6 className="mb-0 me-2">
                                            {supplier.supplier_name}
                                          </h6>
                                          {isSupplierRecommended(supplier) && (
                                            <Badge color="success" className="fs-11">
                                              <i className="ri-thumb-up-line me-1"></i>
                                              Recommended
                                            </Badge>
                                          )}
                                          {supplier.hasPositiveAmount && (
                                            <Badge color="info" className="fs-11">
                                              <i className="ri-money-dollar-circle-line me-1"></i>
                                              Price Available
                                            </Badge>
                                          )}
                                          {item.supplier === supplier.supplier_id && (
                                            <Badge color="info" className="fs-11">
                                              <i className="ri-check-line me-1"></i>
                                              Current Supplier
                                            </Badge>
                                          )}
                                          {supplier.has_previous_orders && (
                                            <div className="d-flex align-items-center">
                                              <Badge color="warning" className="fs-11 me-1">
                                                <i className="ri-history-line me-1"></i>
                                                {supplier.order_count} Previous Order{supplier.order_count !== 1 ? 's' : ''}
                                              </Badge>
                                              <Button
                                                color="link"
                                                size="sm"
                                                className="p-0 ms-1"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleOrderHistoryModal(supplier);
                                                }}
                                                title="View Order History"
                                              >
                                                <i className="ri-information-line fs-16 text-info"></i>
                                              </Button>
                                            </div>
                                          )}
                                        </div>

                                        <div className="text-muted small mb-2">
                                          {supplier.supplier_email && (
                                            <div className="d-flex align-items-center">
                                              <i className="ri-mail-line me-1"></i>
                                              {supplier.supplier_email}
                                            </div>
                                          )}
                                          {supplier.supplier_phone && (
                                            <div className="d-flex align-items-center">
                                              <i className="ri-phone-line me-1"></i>
                                              {supplier.supplier_phone}
                                            </div>
                                          )}
                                        </div>

                                        {/* Modified At Badge for Lowest Cost Section */}
                                        {filterBy === 'lowest_cost' && supplier.modified_at && (
                                          <div className="mb-2">
                                            <Badge color="secondary" className="fs-11">
                                              <i className="ri-time-line me-1"></i>
                                              Updated: {formatDateDisplay(supplier.modified_at)}
                                            </Badge>
                                          </div>
                                        )}

                                        {/* Modified At and Last Ordered Badges for Previous Orders Section */}
                                        {filterBy === 'previous_orders_mapped' && (
                                          <div className="mb-2">
                                            {supplier.modified_at && (
                                              <Badge color="secondary" className="fs-11 me-2">
                                                <i className="ri-time-line me-1"></i>
                                                Updated: {formatDateDisplay(supplier.modified_at)}
                                              </Badge>
                                            )}
                                            {supplier.last_ordered_date && (
                                              <Badge color="info" className="fs-11">
                                                <i className="ri-calendar-line me-1"></i>
                                                Last Ordered: {formatDateDisplay(supplier.last_ordered_date)}
                                              </Badge>
                                            )}
                                          </div>
                                        )}

                                        {IsValidPermissions(["VIEW_ALL_PURCHASE_COST"]) ? (
                                          <div className="price-info">
                                          <div className="d-flex align-items-center flex-wrap">
                                            {supplier.total_amount ? (
                                              <>
                                                <span className="text-dark fw-medium me-2">
                                                  Unit Price: ${supplier.finalAmount.toFixed(2)}
                                                </span>
                                                {supplier.discount > 0 && (
                                                  <span className="text-success fs-12">
                                                    <s className="text-muted me-1">
                                                      ${supplier.total_amount.toFixed(2)}
                                                    </s>
                                                    {supplier.discount}% off
                                                  </span>
                                                )}
                                              </>
                                            ) : (
                                              <span className="text-muted fst-italic">
                                                Price not available
                                              </span>
                                            )}
                                          </div>
                                          {supplier.total_amount && (
                                            <>
                                            <div className="text-primary fw-bold mt-1">
                                              Total: ${calculateRecommendedTotal(
                                                supplier,
                                                selectedStockSourceId ? supplierQtyNum : requestedQty
                                              )} for{" "}
                                              {selectedStockSourceId ? supplierQtyNum : requestedQty} units
                                            </div>
                                            </>
                                          )}
                                        </div>

                                        ) : (
                                          <div className="price-info">
                                            <Alert color="warning" className="fs-11">
                                              <i className="ri-lock-line me-1"></i>
                                                 Price View Restricted
                                            </Alert>
                                            
                                        </div>
                                        ) }
                                      </div>

                                      <div className="d-flex flex-column align-items-end">
                                        {selectedSupplier?.supplier_id ===
                                          supplier.supplier_id && (
                                            <i className="ri-checkbox-circle-fill text-primary fs-18 mb-2"></i>
                                          )}
                                        {isSupplierRecommended(supplier) && (
                                          <Badge color="outline-success" className="fs-10">
                                            Best Price
                                          </Badge>
                                        )}
                                        {supplier.supplier_rating && (
                                          <div className="mt-1">
                                            <small className="text-warning">
                                              {Array.from({ length: 5 }, (_, i) => (
                                                <i
                                                  key={i}
                                                  className={`ri-star-${i < supplier.supplier_rating ? 'fill' : 'line'} fs-12`}
                                                ></i>
                                              ))}
                                            </small>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </CardBody>
                            </Collapse>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="ri-information-line fs-18"></i>
                    <div>
                      No suppliers with available prices for this product
                    </div>
                    <small className="text-muted">
                      All suppliers either don't have pricing information or
                      prices are not available.
                    </small>
                  </div>
                )}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleSave}
              disabled={
                (!isRFQChecked &&
                  ((!selectedStockSourceId && (!selectedSupplier || !totalCost)) ||
                    (selectedStockSourceId && stockQtyNum <= 0) ||
                    (selectedStockSourceId && supplierQtyNum > 0 && !selectedSupplier))) ||
                updating
              }
            >
              {updating ? (
                <Spinner size="sm" />
              ) : isRFQChecked ? (
                "Save as RFQ"
              ) : item.supplier_name ? (
                "Update Supplier"
              ) : (
                "Save Supplier"
              )}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {/* Order History Modal */}
      <Modal isOpen={orderHistoryModal} toggle={() => toggleOrderHistoryModal()} size="xl">
        <ModalHeader toggle={() => toggleOrderHistoryModal()}>
          <i className="ri-history-line me-2"></i>
          Order History - {selectedSupplierForHistory?.supplier_name} & {item.product_name}
        </ModalHeader>
        <ModalBody>
          {orderHistoryLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
              <div className="mt-2 text-muted">Loading order history...</div>
            </div>
          ) : orderHistoryError ? (
            <div className="text-center py-5">
              <i className="ri-error-warning-line fs-48 text-danger"></i>
              <div className="mt-2 text-danger">Failed to load order history</div>
              <Button
                color="primary"
                className="mt-3"
                onClick={refetchOrderHistory}
              >
                Try Again
              </Button>
            </div>
          ) : orderHistoryData ? (
            <div>
              <div className="row mb-4">
                <div className="col-md-6">
                  <Card className="border-0 bg-soft-info">
                    <CardBody>
                      <h6 className="text-info mb-1">
                        <i className="ri-user-line me-2"></i>
                        Supplier
                      </h6>
                      <div className="fw-bold">{orderHistoryData.supplier.name}</div>
                      <small className="text-muted">ID: {orderHistoryData.supplier.id}</small>
                    </CardBody>
                  </Card>
                </div>
                <div className="col-md-6">
                  <Card className="border-0 bg-soft-success">
                    <CardBody>
                      <h6 className="text-success mb-1">
                        <i className="ri-box-line me-2"></i>
                        Product
                      </h6>
                      <div className="fw-bold">{orderHistoryData.product.name}</div>
                      <small className="text-muted">
                        {orderHistoryData.product.code && `Code: ${orderHistoryData.product.code} • `}
                        ID: {orderHistoryData.product.id}
                      </small>
                    </CardBody>
                  </Card>
                </div>
              </div>

              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0">Purchase Orders</h6>
                  <small className="text-muted">
                    Page {orderHistoryData.page} of {orderHistoryData.total_pages}
                  </small>
                </div>
                <Badge color="primary">
                  {orderHistoryData.total_orders_count} Orders Found
                </Badge>
              </div>

              {orderHistoryData.orders.length > 0 ? (
                orderHistoryData.orders.map((order: any) => (
                  <Card key={order.po_id} className="mb-3 border-0 shadow-sm">
                    <CardHeader className="bg-light">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0 text-primary">
                            PO: {order.po_number}
                          </h6>
                          <small className="text-muted">
                            Created: {formatDateTimeDisplay(order.created_date)}
                          </small>
                        </div>
                        <div className="text-end">
                          <Badge
                            color={
                              order.po_status === 'delivered' ? 'success' :
                                order.po_status === 'submitted' ? 'warning' :
                                  order.po_status === 'draft' ? 'secondary' : 'info'
                            }
                          >
                            {order.po_status.toUpperCase()}
                          </Badge>
                          <div className="text-success fw-bold mt-1">
                            <PriceViewComponent price={order.total_cost?.toFixed(2)}/>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardBody>
                      <Table responsive striped>
                        <thead>
                          <tr>
                            <th>Item Status</th>
                            <th>Ordered Qty</th>
                            <th>Delivered Qty</th>
                            <th>Accepted Qty</th>
                            <th>Delivery Status</th>
                            <th>Expected Delivery</th>
                            <th>Actual Delivery</th>
                            <th>Unit Price</th>
                            <th>Total Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.items.map((orderItem: OrderItem) => (
                            <tr key={orderItem.item_id}>
                              <td>
                                <Badge
                                  color={
                                    orderItem.item_status === 'delivered' ? 'success' :
                                      orderItem.item_status === 'pending' ? 'warning' : 'info'
                                  }
                                >
                                  {orderItem.item_status}
                                </Badge>
                              </td>
                              <td className="fw-bold">{orderItem.ordered_quantity}</td>
                              <td>{orderItem.delivered_quantity}</td>
                              <td>{orderItem.accepted_quantity}</td>
                              <td>
                                <Badge
                                  color={
                                    orderItem.delivery_status === 'delivered' ? 'success' :
                                      orderItem.delivery_status === 'partially_delivered' ? 'warning' :
                                        orderItem.delivery_status === 'in_transit' ? 'info' : 'secondary'
                                  }
                                >
                                  {orderItem.delivery_status || 'N/A'}
                                </Badge>
                              </td>
                              <td>
                                {orderItem.expected_delivery_date ?
                                  formatDateDisplay(orderItem.expected_delivery_date) : 'N/A'
                                }
                              </td>
                              <td>
                                {orderItem.actual_delivery_date ?
                                  formatDateDisplay(orderItem.actual_delivery_date) : 'N/A'
                                }
                              </td>
                              <td>
                                {(() => {
                                  // Calculate unit price if not provided or is 0
                                  let unitPrice = orderItem.unit_price;
                                  if (!unitPrice || unitPrice === 0) {
                                    const totalPrice = parseFloat(orderItem.total_price?.toString() || "0");
                                    const orderedQty = parseFloat(orderItem.ordered_quantity?.toString() || "0");
                                    if (orderedQty > 0) {
                                      unitPrice = totalPrice / orderedQty;
                                    } else {
                                      unitPrice = 0;
                                    }
                                  }
                                  return <PriceViewComponent price={unitPrice?.toFixed(2)}/>;
                                })()}
                              </td>
                              <td className="fw-bold text-success">
                                <PriceViewComponent price={orderItem.total_price?.toFixed(2)}/>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                ))
              ) : (
                <div className="text-center py-5">
                  <i className="ri-information-line fs-48 text-muted"></i>
                  <div className="mt-2 text-muted">No previous orders found</div>
                  <small className="text-muted">
                    This supplier hasn't delivered this product before.
                  </small>
                </div>
              )}
              {orderHistoryData.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted small">
                    Showing page {orderHistoryData.page} of {orderHistoryData.total_pages}
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      size="sm"
                      color="light"
                      disabled={orderHistoryPage <= 1 || orderHistoryLoading}
                      onClick={async () => {
                        if (orderHistoryPage <= 1) return;
                        if (!selectedSupplierForHistory || !item.product) return;
                        const nextPage = orderHistoryPage - 1;
                        setOrderHistoryPage(nextPage);
                        try {
                          const result = await getPurchaseOrdersByProductAndSupplier({
                            supplier_id: selectedSupplierForHistory.supplier_id,
                            product_id: item.product,
                            page: nextPage,
                            page_size: orderHistoryPageSize,
                          }).unwrap();
                          setOrderHistoryData(result as OrderData);
                        } catch (error) {
                          console.error("Failed to load previous page of order history:", error);
                        }
                      }}
                    >
                      <i className="ri-arrow-left-s-line me-1" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      color="light"
                      disabled={
                        orderHistoryPage >= (orderHistoryData.total_pages || 1) ||
                        orderHistoryLoading
                      }
                      onClick={async () => {
                        if (!selectedSupplierForHistory || !item.product) return;
                        const nextPage = orderHistoryPage + 1;
                        setOrderHistoryPage(nextPage);
                        try {
                          const result = await getPurchaseOrdersByProductAndSupplier({
                            supplier_id: selectedSupplierForHistory.supplier_id,
                            product_id: item.product,
                            page: nextPage,
                            page_size: orderHistoryPageSize,
                          }).unwrap();
                          setOrderHistoryData(result as OrderData);
                        } catch (error) {
                          console.error("Failed to load next page of order history:", error);
                        }
                      }}
                    >
                      Next
                      <i className="ri-arrow-right-s-line ms-1" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="ri-information-line fs-48 text-muted"></i>
              <div className="mt-2 text-muted">No data available</div>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => toggleOrderHistoryModal()}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PurchaseOrderProduct;